import React, { useCallback, useEffect, useState } from 'react';
import { Expand, Shrink } from 'lucide-react';
import './ReportsNative.css';

/**
 * Fullscreen card support for the report tables.
 *
 * useFullscreenCard() drives a fixed-overlay "rn-card-fs" state: Escape
 * exits, body scroll locks while open. Components render a
 * <FullscreenBackdrop> sibling before their card section and add
 * fsSectionClass(isFullscreen) to the card's className.
 */
export function useFullscreenCard() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggle = useCallback(() => setIsFullscreen((v) => !v), []);
  const exit = useCallback(() => setIsFullscreen(false), []);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  return { isFullscreen, toggle, exit };
}

export function FullscreenButton({ isFullscreen, onToggle }) {
  return (
    <button
      type="button"
      className="rn-icon-btn"
      title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
      aria-pressed={isFullscreen}
      onClick={onToggle}
    >
      {isFullscreen ? <Shrink /> : <Expand />}
    </button>
  );
}

export function FullscreenBackdrop({ active, onClose }) {
  if (!active) return null;
  return <div className="rn-fs-backdrop" onClick={onClose} aria-hidden="true" />;
}

export default useFullscreenCard;
